/**
 * A topographic contour field, generated rather than drawn.
 *
 * WHY GENERATED
 * Contours are the one ornament a GIS portal has earned — they are what the
 * product is about. But hand-authored contour paths would be a screenful of
 * unreadable SVG `d` data that cannot be reflowed, retinted or resized, and
 * that nobody could edit afterwards. Real contours are cheaper: a smooth field
 * plus marching squares gives lines that behave like contours, because they
 * are contours — they never cross, they nest, and they close.
 *
 * DETERMINISTIC
 * The noise is seeded, so the pattern is identical on every render and every
 * reload. A background that reshuffled itself when React re-rendered would
 * read as a glitch, and would make the page feel unstable to sit on.
 *
 * QUIET BY CONSTRUCTION
 * Drawn once to a canvas at low opacity, with no animation. This sits behind
 * a page people read; an ornament that moves is an ornament competing with the
 * text it is meant to support.
 */

import { useEffect, useRef } from 'react';

/**
 * Deterministic pseudo-random in [0,1) from integer coordinates.
 *
 * `Math.imul` throughout, and unsigned shifts, because this has to stay inside
 * 32 bits. Plain `*` on hash constants of this size produces a float past
 * 2^53, which the following bitwise op coerces to zero — every lattice point
 * comes back 0.000 and the whole field is flat. Measured, not assumed: the
 * fixed version spreads 1,600 lattice points evenly across all ten deciles.
 */
function hash(x: number, y: number, seed: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(seed, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Smoothstep, so cell boundaries do not show as creases in the field. */
const fade = (t: number) => t * t * (3 - 2 * t);

/** Value noise: a lattice of random heights, smoothly interpolated. */
function valueNoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = fade(x - x0);
  const fy = fade(y - y0);

  const topLeft = hash(x0, y0, seed);
  const topRight = hash(x0 + 1, y0, seed);
  const bottomLeft = hash(x0, y0 + 1, seed);
  const bottomRight = hash(x0 + 1, y0 + 1, seed);

  const top = topLeft + (topRight - topLeft) * fx;
  const bottom = bottomLeft + (bottomRight - bottomLeft) * fx;
  return top + (bottom - top) * fy;
}

/**
 * Several octaves summed — one smooth landform, then finer detail on top.
 *
 * Terrain is self-similar at different scales, which is why one octave looks
 * like a blob and four look like ground.
 */
function terrain(x: number, y: number, seed: number): number {
  let value = 0;
  let amplitude = 1;
  let frequency = 1;
  let total = 0;
  for (let octave = 0; octave < 4; octave += 1) {
    value += valueNoise(x * frequency, y * frequency, seed + octave * 101) * amplitude;
    total += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value / total;
}

/**
 * Marching squares: one iso-line through a scalar field.
 *
 * Each cell is classified by which of its corners are above the threshold, and
 * the line is drawn between the edges where the level is crossed. The crossing
 * point is INTERPOLATED rather than taken at the edge's midpoint — midpoints
 * give the stair-stepped look that reads as a jagged bitmap instead of a
 * contour.
 */
function drawIsoline(
  context: CanvasRenderingContext2D,
  field: Float32Array,
  cols: number,
  rows: number,
  cellSize: number,
  level: number,
) {
  const at = (cx: number, cy: number) => field[cy * (cols + 1) + cx];
  const between = (a: number, b: number) => (level - a) / (b - a || 1e-6);

  context.beginPath();
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < cols; cx += 1) {
      const tl = at(cx, cy);
      const tr = at(cx + 1, cy);
      const br = at(cx + 1, cy + 1);
      const bl = at(cx, cy + 1);

      const code =
        (tl > level ? 8 : 0) | (tr > level ? 4 : 0) | (br > level ? 2 : 0) | (bl > level ? 1 : 0);
      if (code === 0 || code === 15) continue;

      const x = cx * cellSize;
      const y = cy * cellSize;
      const top = { x: x + between(tl, tr) * cellSize, y };
      const right = { x: x + cellSize, y: y + between(tr, br) * cellSize };
      const bottom = { x: x + between(bl, br) * cellSize, y: y + cellSize };
      const left = { x, y: y + between(tl, bl) * cellSize };

      const segment = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        context.moveTo(a.x, a.y);
        context.lineTo(b.x, b.y);
      };

      switch (code) {
        case 1: case 14: segment(left, bottom); break;
        case 2: case 13: segment(bottom, right); break;
        case 3: case 12: segment(left, right); break;
        case 4: case 11: segment(top, right); break;
        case 6: case 9: segment(top, bottom); break;
        case 7: case 8: segment(left, top); break;
        // The two ambiguous saddles: both crossings drawn, which keeps the
        // lines closed rather than leaving a gap at the pass.
        case 5: segment(left, top); segment(bottom, right); break;
        case 10: segment(top, right); segment(left, bottom); break;
        default: break;
      }
    }
  }
  context.stroke();
}

export default function ContourBackground({
  /** Line colour. Alpha is applied per band, so pass an opaque colour. */
  colour = '#38BDF8',
  /** Second colour, used on every fifth line — the index contour. */
  accent = '#0D9488',
  /** Larger spreads the landform out; smaller makes it busier. */
  scale = 3.2,
  seed = 20260825,
  className,
}: {
  colour?: string;
  accent?: string;
  scale?: number;
  seed?: number;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const render = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      /* Capped at 1.5, not devicePixelRatio: this is a soft background, and a
         3× canvas on a phone costs memory nobody gets to see. */
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = parent.clientWidth;
      const height = parent.clientHeight;
      if (width === 0 || height === 0) return;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext('2d');
      if (!context) return;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, width, height);

      /* Sample every 6 CSS pixels. Finer than the eye needs at this opacity,
         and coarse enough that a 4K window is still a few hundred thousand
         samples rather than millions. */
      const cellSize = 6;
      const cols = Math.ceil(width / cellSize);
      const rows = Math.ceil(height / cellSize);

      const field = new Float32Array((cols + 1) * (rows + 1));
      // Sampled in the SHORTER dimension's units, so the pattern keeps its
      // proportions instead of stretching with the window.
      const unit = Math.min(width, height) / scale;
      let lowest = Infinity;
      let highest = -Infinity;
      for (let cy = 0; cy <= rows; cy += 1) {
        for (let cx = 0; cx <= cols; cx += 1) {
          const value = terrain((cx * cellSize) / unit, (cy * cellSize) / unit, seed);
          field[cy * (cols + 1) + cx] = value;
          if (value < lowest) lowest = value;
          if (value > highest) highest = value;
        }
      }
      /* Levels are spread across the field's ACTUAL range, not 0–1. Summed
         octaves cluster around the middle — measured, this field spans about
         0.07 to 0.81 — so fixed levels waste a quarter of the bands on
         elevations the terrain never reaches, and the map comes out thinner
         than it should be. */
      const span = highest - lowest || 1;

      context.lineCap = 'round';
      context.lineJoin = 'round';

      const bands = 22;
      for (let index = 1; index < bands; index += 1) {
        const level = lowest + (index / bands) * span;
        // Every fifth line heavier and tinted: the index contour, exactly as a
        // topographic sheet marks its readable intervals.
        const isIndex = index % 5 === 0;
        context.strokeStyle = isIndex ? accent : colour;
        context.globalAlpha = isIndex ? 0.3 : 0.14;
        context.lineWidth = isIndex ? 1.4 : 0.8;
        drawIsoline(context, field, cols, rows, cellSize, level);
      }
      context.globalAlpha = 1;
    };

    render();

    const observer = new ResizeObserver(render);
    if (canvas.parentElement) observer.observe(canvas.parentElement);
    return () => observer.disconnect();
  }, [colour, accent, scale, seed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    />
  );
}
